import { Calendar, Check, Clock, History, Plus, Search, Star, Trophy, X } from 'lucide-react'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import FilterDrawer from '../../components/FilterDrawer'
import Layout from '../../components/Layout'
import TournamentCard from '../../components/TournamentCard'
import { Button } from '../../components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { useUser } from '../../contexts/UserContext'
import {
  addTournamentFavorite,
  fetchSports as fetchSportsService,
  fetchTournamentFavorites,
  fetchTournaments as fetchTournamentsService,
  removeTournamentFavorite,
} from '../../services/tournamentService'
import { compareTournaments, getTournamentTimeStatus } from '../../lib/tournamentSort'

export default function TournamentManagement({ embedded = false }) {
  const Wrapper = embedded ? Fragment : Layout
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useUser()
  const [tournaments, setTournaments] = useState([])
  const [loading, setLoading] = useState(true)
  const [sports, setSports] = useState([])
  const [favoriteTournaments, setFavoriteTournaments] = useState(new Set())
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  
  // Initialize filters from URL params or localStorage
  const getInitialSportPreferences = () => {
    const saved = localStorage.getItem('sportPreferences')
    if (saved) {
      try {
        return JSON.parse(saved)
      } catch {
        return []
      }
    }
    return []
  }
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [filterSport, setFilterSport] = useState(searchParams.get('sport') || 'all')
  const [startDateFrom, setStartDateFrom] = useState(searchParams.get('from') || '')
  const [startDateTo, setStartDateTo] = useState(searchParams.get('to') || '')
  const [activeTimeFilter, setActiveTimeFilter] = useState(null)
  const [selectedSports, setSelectedSports] = useState(getInitialSportPreferences)
  
  // Debounce timer ref for search
  const searchTimeoutRef = useRef(null)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '')
  
  const isAdmin = user?.role === 'admin'

  // Debounce search term
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm])

  // Update URL params when filters change. Preserve any unrelated params (e.g. the
  // `tab` param set by AdminDashboard) by cloning the current searchParams and only
  // managing the keys this page owns.
  useEffect(() => {
    const params = new URLSearchParams(searchParams)
    if (debouncedSearchTerm) params.set('search', debouncedSearchTerm)
    else params.delete('search')
    if (filterSport !== 'all') params.set('sport', filterSport)
    else params.delete('sport')
    if (startDateFrom) params.set('from', startDateFrom)
    else params.delete('from')
    if (startDateTo) params.set('to', startDateTo)
    else params.delete('to')

    setSearchParams(params, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, filterSport, startDateFrom, startDateTo, setSearchParams])

  // Fetch tournaments when filters change. sports (the loaded sport list) is
  // intentionally NOT a dep here — it loads async on mount and would
  // otherwise force a second fetch right after the first, causing the
  // loader to flicker. filterSport already holds the sport UUID.
  const fetchTournaments = useCallback(async () => {
    try {
      setLoading(true)

      const sportId = filterSport !== 'all' ? filterSport : null

      const data = await fetchTournamentsService({
        searchTerm: debouncedSearchTerm,
        sportId,
        status: null,
        startDateFrom,
        startDateTo
      })
      
      // Transform data to match expected format
      const transformedData = (data || []).map(t => ({
        id: t.id,
        title: t.name,
        startDate: t.startDate,
        endDate: t.endDate,
        submissionDeadline: t.submissionDeadline,
        status: t.status,
        isTest: !!t.isTest,
        sportName: t.sport?.name || null,
        sportId: t.sport?.id || null,
        divisions: t.bracketCount || 0,
        // Bracket statuses drive the Live→Past flip once all official
        // results are in (DUB-184).
        brackets: t.brackets || [],
        }))
      
      setTournaments(transformedData)
    } catch (err) {
      console.error('Error fetching tournaments:', err)
    } finally {
      setLoading(false)
    }
  }, [debouncedSearchTerm, filterSport, startDateFrom, startDateTo])

  // Select sport filter
  const handleSportSelect = (value) => {
    const newSelection = value === 'all' ? [] : [value]
    localStorage.setItem('sportPreferences', JSON.stringify(newSelection))
    setSelectedSports(newSelection)
  }

  // Fetch user's favorite tournaments
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!user?.id) return
      
      try {
        const data = await fetchTournamentFavorites({ userId: user.id })
        const favoriteIds = new Set(data?.map(f => f.tournament_id) || [])
        setFavoriteTournaments(favoriteIds)
      } catch (err) {
        console.error('Error fetching favorites:', err)
      }
    }
    fetchFavorites()
  }, [user?.id])

  // Fetch sports on mount
  useEffect(() => {
    const fetchSports = async () => {
      try {
        const data = await fetchSportsService()
        setSports(data || [])
      } catch (err) {
        console.error('Error fetching sports:', err)
        setSports([])
      }
    }
    fetchSports()
  }, [])

  // Fetch tournaments when filters or sports change
  useEffect(() => {
    fetchTournaments()
  }, [fetchTournaments])

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setFilterSport('all')
    setStartDateFrom('')
    setStartDateTo('')
    setActiveTimeFilter(null)
  }

  // Toggle tournament favorite
  const toggleFavorite = async (tournamentId, e) => {
    if (e?.stopPropagation) e.stopPropagation()
    if (!user?.id) return
    
    const isFav = favoriteTournaments.has(tournamentId)
    
    try {
      if (isFav) {
        await removeTournamentFavorite({ userId: user.id, tournamentId })
        setFavoriteTournaments(prev => {
          const next = new Set(prev)
          next.delete(tournamentId)
          return next
        })
      } else {
        await addTournamentFavorite({ userId: user.id, tournamentId })
        setFavoriteTournaments(prev => new Set([...prev, tournamentId]))
      }
    } catch (err) {
      console.error('Error toggling favorite:', err)
    }
  }

  const sportFilteredTournaments = useMemo(() => {
    return tournaments.filter(t => {
      return selectedSports.length === 0 || selectedSports.includes(t.sportId)
    })
  }, [tournaments, selectedSports])

  const getFilteredForTab = (tabValue) => {
    let filtered = sportFilteredTournaments
    if (tabValue === 'favorites') {
      filtered = filtered.filter(t => favoriteTournaments.has(t.id))
    } else if (tabValue) {
      filtered = filtered.filter(t => getTournamentTimeStatus(t) === tabValue)
    }
    return [...filtered].sort((a, b) => compareTournaments(a, b, { tabValue }))
  }

  const FILTER_OPTIONS = [
    { value: 'live', label: 'Live' },
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'favorites', label: 'Favorites' },
    { value: 'past', label: 'Past' },
  ]

  const getFilterPillClass = (value) => {
    const isSelected = activeTimeFilter === value
    return `inline-flex items-center gap-3 px-5 py-2.5 rounded-full text-sm font-medium transition-all border focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent ${
      isSelected
        ? 'bg-[var(--color-royal-purple)] text-[var(--color-lavender)] border-[var(--color-purple)] shadow-pill-active'
        : 'bg-[color-mix(in_srgb,var(--color-purple)_3%,white)] border-[color-mix(in_srgb,var(--color-purple)_10%,white)] text-[var(--color-ink)] shadow-pill-inactive hover:bg-[color-mix(in_srgb,var(--color-purple)_6%,white)] hover:shadow-pill-inactive-hover'
    }`
  }

  const handleFilterSelect = (filterValue) => {
    setStartDateFrom('')
    setStartDateTo('')
    setActiveTimeFilter(prev => prev === filterValue ? null : filterValue)
  }

  const filteredTournaments = getFilteredForTab(activeTimeFilter)

  const EMPTY_STATE_CONFIG = {
    all:       { icon: Trophy,  message: 'No tournaments found',           description: 'Check back later for new tournaments' },
    live:      { icon: Trophy,  message: 'No live tournaments',            description: 'There are no tournaments in progress right now' },
    upcoming:  { icon: Clock,   message: 'No upcoming tournaments',        description: 'New tournaments will appear here when scheduled' },
    favorites: { icon: Star,    message: 'No favorite tournaments',        description: 'Star a tournament to add it to your favorites' },
    past:      { icon: History, message: 'No past tournaments',            description: 'Completed tournaments will appear here' },
  }

  const renderTournamentList = () => {
    if (loading) {
      return (
        <div className="rounded-lg border bg-card/60 backdrop-blur-sm flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading tournaments...</p>
          </div>
        </div>
      )
    }

    if (filteredTournaments.length === 0) {
      const config = EMPTY_STATE_CONFIG[activeTimeFilter] || EMPTY_STATE_CONFIG.all
      const Icon = config.icon

      return (
        <div className="rounded-lg border bg-card/60 backdrop-blur-sm flex items-center justify-center py-16">
          <div className="text-center">
            <Icon className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-lg text-muted-foreground mb-1">{config.message}</p>
            <p className="text-sm text-muted-foreground">{config.description}</p>
            {embedded && isAdmin && tournaments.length === 0 && (
              <Button variant="primaryNeon" className="mt-4" onClick={() => navigate('/tournaments/create')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Tournament
              </Button>
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-3">
        {filteredTournaments.map((tournament) => (
          <TournamentCard
            key={tournament.id}
            tournament={tournament}
            variant="compact"
            isFavorite={favoriteTournaments.has(tournament.id)}
            onToggleFavorite={toggleFavorite}
            isAdminContext={embedded}
          />
        ))}
      </div>
    )
  }

  return (
    <Wrapper>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">Tournaments</h1>
            <p className="text-muted-foreground">Once a bracket is submitted, the event will appear on the &ldquo;My Tournaments&rdquo; list on the Home page.</p>
          </div>
          {embedded && isAdmin && (
            <Button
              variant="primaryNeon"
              onClick={() => navigate('/tournaments/create')}
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Tournament
            </Button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="mb-4">
          <div className="flex flex-wrap items-center gap-3">
            {FILTER_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={getFilterPillClass(value)}
                onClick={() => handleFilterSelect(value)}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  activeTimeFilter === value
                    ? '!border-white bg-transparent'
                    : 'border-[var(--mauve-6)] bg-transparent'
                }`}>
                  {activeTimeFilter === value && (
                    <Check className="h-3 w-3 text-[var(--color-white)]" strokeWidth={3} />
                  )}
                </div>
                {label}
              </button>
            ))}
            {activeTimeFilter !== null && (
              <button
                type="button"
                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors ml-2 whitespace-nowrap"
                onClick={() => {
                  setStartDateFrom('')
                  setStartDateTo('')
                  setActiveTimeFilter(null)
                }}
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Search & Filter Tools */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2 mb-4">
          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search tournaments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border rounded-md bg-background text-sm h-10"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {sports.length > 0 && (
            <Select value={selectedSports[0] || 'all'} onValueChange={handleSportSelect}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue>
                  {selectedSports.length === 0
                    ? 'All Sports'
                    : sports.find(s => s.id === selectedSports[0])?.name || 'All Sports'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                {sports.map((sport) => (
                  <SelectItem key={sport.id} value={sport.id}>
                    {sport.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="w-full sm:w-auto sm:ml-auto">
            <FilterDrawer
              open={filterDrawerOpen}
              onOpenChange={setFilterDrawerOpen}
              title="Filter Tournaments"
              description="Refine your tournament results using the filters below"
              activeFilterCount={[
                filterSport !== 'all',
                startDateFrom,
                startDateTo
              ].filter(Boolean).length}
              onClearFilters={clearFilters}
            >
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">Sport</label>
                <Select value={filterSport} onValueChange={setFilterSport}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Sports" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sports</SelectItem>
                    {sports.map((sport) => (
                      <SelectItem key={sport.id} value={sport.id}>
                        {sport.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">Start Date From</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={startDateFrom}
                    onChange={(e) => {
                      setStartDateFrom(e.target.value)
                      setActiveTimeFilter(null)
                    }}
                    className="w-full pl-10 pr-3 py-2 border rounded-md bg-background text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">Start Date To</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="date"
                    value={startDateTo}
                    onChange={(e) => {
                      setStartDateTo(e.target.value)
                      setActiveTimeFilter(null)
                    }}
                    className="w-full pl-10 pr-3 py-2 border rounded-md bg-background text-sm"
                  />
                </div>
              </div>
            </FilterDrawer>
          </div>
        </div>

        {/* Tournament List */}
        {renderTournamentList()}
      </div>
    </Wrapper>
  )
}
